import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Download, Trash2, Upload, FileText, MessageCircle, Clock, History, Send } from 'lucide-react';
import Header from '../../components/Header';
import Footer from '../../components/Footer';
import { api, API } from '../../lib/api';
import { statusLabel, statusTone, bytesToHuman, fmtDateTime, downloadFromLink } from '../../lib/docs';
import { toast } from 'sonner';
import PdfViewer from '../../components/PdfViewer';

export default function MemberDocumentDetailPage() {
  const { id } = useParams();
  const [doc, setDoc] = useState(null);
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [reply, setReply] = useState('');
  const [busy, setBusy] = useState(false);
  const [viewLink, setViewLink] = useState(null);
  const fileRef = useRef();
  const nav = useNavigate();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [d, n] = await Promise.all([api.myDocument(id), api.myDocumentNotes(id)]);
      setDoc(d); setNotes(n);
      if (/pdf/i.test(d.mime || '')) {
        try { setViewLink(await api.myDocumentViewLink(id)); } catch { /* fallback to download */ }
      } else {
        setViewLink(null);
      }
    } catch {
      toast.error('Document not available');
      nav('/account/documents');
    } finally { setLoading(false); }
  }, [id, nav]);
  useEffect(() => { load(); }, [load]);

  const download = async () => {
    try {
      const { url } = await api.myDocumentDownloadLink(id);
      downloadFromLink(url, API.replace(/\/api$/, ''));
    } catch { toast.error('Could not generate download link'); }
  };

  const submitReply = async () => {
    if (!reply.trim()) return;
    try {
      setBusy(true);
      await api.myDocumentNoteCreate(id, reply.trim());
      setReply('');
      const fresh = await api.myDocumentNotes(id);
      setNotes(fresh);
    } catch { toast.error('Could not post comment'); }
    finally { setBusy(false); }
  };

  const uploadNewVersion = async (file) => {
    if (!file) return;
    const fd = new FormData(); fd.append('file', file);
    try {
      setBusy(true);
      await api.myDocumentVersionUpload(id, fd);
      toast.success('New version uploaded');
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Upload failed');
    } finally { setBusy(false); }
  };

  const onDelete = async () => {
    if (!confirm('Delete this document? You can re-upload it later.')) return;
    try {
      await api.myDocumentDelete(id);
      toast.success('Document deleted');
      nav('/account/documents');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not delete');
    }
  };

  if (loading || !doc) {
    return (<><Header /><div className="py-20 text-center text-neutral-500">Loading…</div><Footer /></>);
  }

  return (
    <>
      <Header />
      <section className="py-10 bg-[#0c0a08] border-b border-neutral-800 text-white">
        <div className="max-w-[1100px] mx-auto px-6">
          <Link to="/account/documents" data-testid="back-to-docs" className="text-[12px] uppercase tracking-wider text-[#f5b300] inline-flex items-center gap-1 hover:underline mb-2">
            <ArrowLeft className="w-3.5 h-3.5" /> Back to documents
          </Link>
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div className="min-w-0">
              <h1 className="text-[26px] md:text-[30px] font-extrabold text-[#f5b300] truncate">{doc.original_name}</h1>
              <p className="text-[13px] text-neutral-400 mt-1">{doc.category_name} · Tax year {doc.tax_year} · {bytesToHuman(doc.size)}</p>
            </div>
            <span className={`inline-flex items-center px-3 py-1.5 rounded-full text-[12px] font-semibold ${statusTone(doc.status)}`}>
              {statusLabel(doc.status)}
            </span>
          </div>
        </div>
      </section>

      <section className="py-10 bg-white">
        <div className="max-w-[1100px] mx-auto px-6 grid grid-cols-1 lg:grid-cols-3 gap-7">
          <div className="lg:col-span-2 space-y-6">
            {viewLink && (
              <PdfViewer viewUrl={viewLink.url}
                         fileName={doc.original_name}
                         mime={doc.mime}
                         documentId={doc.id}
                         canDownload
                         canPrint
                         className="min-h-[640px]" />
            )}
            <div className="rounded-xl border border-neutral-100 bg-white p-6">
              <div className="flex flex-wrap gap-3">
                <button onClick={download} data-testid="download-doc" className="inline-flex items-center gap-2 bg-[#f5b300] hover:bg-[#e0a200] text-[#14110f] font-semibold px-4 py-2.5 rounded-md text-[13px]">
                  <Download className="w-4 h-4" /> Download
                </button>
                <button onClick={() => fileRef.current?.click()} disabled={busy || doc.locked} data-testid="upload-version"
                        className="inline-flex items-center gap-2 border border-neutral-200 hover:border-neutral-300 hover:bg-neutral-50 text-[#14110f] font-semibold px-4 py-2.5 rounded-md text-[13px] disabled:opacity-40">
                  <Upload className="w-4 h-4" /> Upload new version
                </button>
                <input ref={fileRef} type="file" className="hidden" onChange={(e) => uploadNewVersion(e.target.files?.[0])} />
                {!doc.locked && (
                  <button onClick={onDelete} data-testid="delete-doc" className="inline-flex items-center gap-2 border border-rose-100 text-rose-600 hover:bg-rose-50 font-semibold px-4 py-2.5 rounded-md text-[13px]">
                    <Trash2 className="w-4 h-4" /> Delete
                  </button>
                )}
              </div>
              <dl className="mt-6 grid grid-cols-2 gap-x-6 gap-y-3 text-[13.5px]">
                <div><dt className="text-[11px] uppercase tracking-wider text-neutral-500">Uploaded</dt><dd className="text-[#14110f]">{fmtDateTime(doc.uploaded_at)}</dd></div>
                <div><dt className="text-[11px] uppercase tracking-wider text-neutral-500">Uploaded by</dt><dd className="text-[#14110f]">{doc.uploaded_by_name || '—'}</dd></div>
                <div><dt className="text-[11px] uppercase tracking-wider text-neutral-500">Version</dt><dd className="text-[#14110f]">v{doc.version}</dd></div>
                <div><dt className="text-[11px] uppercase tracking-wider text-neutral-500">Type</dt><dd className="text-[#14110f]">{doc.mime}</dd></div>
              </dl>
              {doc.description && (
                <div className="mt-5 p-3.5 rounded-md bg-[#fbf7ee] text-[13.5px] text-neutral-700">
                  <p className="text-[10.5px] uppercase tracking-wider text-neutral-500 mb-0.5">Your notes</p>
                  {doc.description}
                </div>
              )}
            </div>

            <div className="rounded-xl border border-neutral-100 bg-white p-6">
              <h3 className="text-[16px] font-bold text-[#14110f] flex items-center gap-2 mb-4"><MessageCircle className="w-4 h-4 text-[#c89200]" /> Conversation with your tax specialist</h3>
              {notes.length === 0 ? <p className="text-[13px] text-neutral-500">No comments yet from your tax specialist.</p>
                : <ul className="space-y-3" data-testid="notes-list">
                    {notes.map((n) => (
                      <li key={n.id} className={`rounded-lg p-3.5 ${n.author_role === 'member' ? 'bg-[#fbf7ee]' : 'bg-neutral-50 border border-neutral-100'}`}>
                        <p className="text-[11px] text-neutral-500 mb-1">
                          <span className="font-semibold text-[#14110f]">{n.author_name}</span> · {fmtDateTime(n.created_at)}{n.edited_at ? ' (edited)' : ''}
                        </p>
                        <p className="text-[13.5px] whitespace-pre-wrap text-[#14110f]">{n.body}</p>
                      </li>
                    ))}
                  </ul>}
              <div className="mt-4 flex gap-2">
                <input data-testid="reply-input" value={reply} onChange={(e) => setReply(e.target.value)}
                       placeholder="Reply to your tax specialist…"
                       className="flex-1 px-3 py-2.5 rounded-md border border-neutral-200 text-[13.5px] focus:border-[#f5b300] outline-none" />
                <button data-testid="reply-send" onClick={submitReply} disabled={busy} className="inline-flex items-center gap-2 bg-[#14110f] hover:bg-[#291d12] text-white font-semibold px-4 py-2.5 rounded-md text-[13px]">
                  <Send className="w-4 h-4" /> Send
                </button>
              </div>
            </div>
          </div>

          <aside className="space-y-5">
            <div className="rounded-xl border border-neutral-100 bg-white p-5">
              <h3 className="text-[14px] font-bold text-[#14110f] mb-3 flex items-center gap-2"><History className="w-4 h-4 text-[#c89200]" /> Version history</h3>
              <ul className="space-y-2">
                {(doc.versions || []).slice().reverse().map((v) => (
                  <li key={v.id} className="text-[12.5px] text-neutral-700 flex items-center justify-between">
                    <span>v{v.version} · {bytesToHuman(v.size)}</span>
                    <span className="text-neutral-400">{fmtDateTime(v.uploaded_at)}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="rounded-xl border border-neutral-100 bg-white p-5">
              <h3 className="text-[14px] font-bold text-[#14110f] mb-3 flex items-center gap-2"><Clock className="w-4 h-4 text-[#c89200]" /> Status timeline</h3>
              <ul className="space-y-2.5">
                {(doc.status_history || []).slice().reverse().map((s, i) => (
                  <li key={i} className="text-[12.5px] text-neutral-700">
                    <p><span className="font-semibold text-[#14110f]">{statusLabel(s.status)}</span> · {fmtDateTime(s.at)}</p>
                    {s.message && <p className="text-neutral-500 mt-0.5">{s.message}</p>}
                    {s.changed_by_name && <p className="text-neutral-400 text-[11px]">by {s.changed_by_name}</p>}
                  </li>
                ))}
              </ul>
            </div>
          </aside>
        </div>
      </section>
      <Footer />
    </>
  );
}
